/**
 * ProtectedRoute — HOC that gates children behind Firebase auth.
 *
 * Behavior:
 *   isLoading        → full-page spinner (Spinner atom, size lg)
 *   !isAuthenticated → redirect to /login?from={currentPathname}
 *   isAuthenticated  → render children
 *
 * Usage:
 *   <ProtectedRoute><Dashboard /></ProtectedRoute>
 *
 * The `from` query param lets the Login page navigate back after sign-in.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/atoms/Spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const from = encodeURIComponent(location.pathname + location.search);
      void navigate(`/login?from=${from}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[var(--auth-surface-page)]">
        <Spinner size="lg" label="Authenticating" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect is in flight via useEffect — render nothing to avoid flash
    return null;
  }

  return <>{children}</>;
}
